import React, { useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import { useIntl } from 'umi';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import './index.scss';
import { useVoiceReportingTextAnalysisStats } from '@op/pages/report/hooks/useVoiceReport';
import MultiRingRatioBubble from '../MultiRingRatioBubble';
import {
  dataGap,
  formatDateFunc,
  getFilterDateQOQText,
  isSameTime,
} from '@op/utils/time';
import MetricChart from '../MetricChart';
import { DBO_V } from '@op/utils/format';

interface IMetricsList {
  filter: any;
  title: string;
  statsType: string;
}
export default function MetricsList(props: IMetricsList) {
  const { formatMessage } = useIntl();
  const { statsType, filter, title } = props;

  const filterParams = useMemo(
    () => ({
      ...filter,
      ...(statsType ? { stats_type: statsType } : {}),
    }),
    [filter, statsType],
  );

  const { text: beforeText } = getFilterDateQOQText(filter);

  const beforeChartText = useMemo(() => {
    const { date_type } = filter;
    const map: any = {
      daily: 'Today',
      weekly: 'This Week',
      monthly: 'This Month',
      hourly: 'This Hour',
    };

    return map[date_type] || 'Today';
  }, [filter]);

  const sameDayLastWeek = useMemo(() => {
    switch (filter.date_type) {
      case 'daily':
        return 'SDLW';
      case 'weekly':
        return 'SWLM';
      case 'monthly':
        return 'SMLY';
      case 'hourly':
        return 'SHLD';
      default:
        return 'SDLW';
    }
  }, [filter.date_type]);

  const { data, loading } = useVoiceReportingTextAnalysisStats({
    filter: filterParams,
    extraData: {
      beforeText: beforeChartText,
      sameDayLastWeek: sameDayLastWeek,
    },
  });

  const {
    selectedCount,
    multiRatio,
    dodRatio,
    sdlwRatio,
    xAxisData,
    seriesData,
    showSDLW,
  } = useMemo(() => {
    const {
      selectedCount,
      selectedTime: baseTime,
      dodValue,
      dodTime,
      sdlwValue,
      sdlwTime,
      dodRatio,
      sdlwRatio,
      xAxisData,
      seriesData = [],
    } = data || {};

    const { date_type, start_time, end_time } = filter;
    let showSDLW = isSameTime(date_type)(
      formatDateFunc(date_type)(start_time),
      formatDateFunc(date_type)(end_time),
    );

    if (date_type === 'weekly') {
      showSDLW = dataGap(start_time, end_time) === 6;
    }

    const multiRatio = [
      {
        title: formatMessage({ id: beforeText }),
        newTime: baseTime,
        oldTime: dodTime,
        oldValue: dodValue,
        newValue: selectedCount,
        formatDate: formatDateFunc(date_type || 'daily'),
      },
    ];

    if (showSDLW) {
      multiRatio.push({
        title: formatMessage({ id: sameDayLastWeek }),
        newTime: baseTime,
        oldTime: sdlwTime,
        oldValue: sdlwValue,
        newValue: selectedCount,
        formatDate: formatDateFunc(date_type || 'daily'),
      });
    }

    return {
      selectedCount,
      multiRatio,
      dodRatio,
      sdlwRatio,
      xAxisData,
      seriesData,
      showSDLW,
    };
  }, [beforeText, data, filter, formatMessage, sameDayLastWeek]);

  return (
    <CardCommon
      className="dbo-voice-keyMetric-item"
      title={formatMessage({ id: title })}
      loading={loading}
      loadingHeight={70}
    >
      <div className="dbo-voice-keyMetric-flex">
        <div className="dbo-voice-keyMetric-info">
          <div className="dbo-voice-keyMetric-info-title">
            {DBO_V.formatThousand(selectedCount)}
          </div>
          <MultiRingRatioBubble data={multiRatio} placement={'bottom'}>
            <>
              <div className="dbo-cardcontext-flex">
                <span className="dbo-context-text">
                  {formatMessage({ id: beforeText })}
                </span>
                <PercentageItem percentage={dodRatio}></PercentageItem>
              </div>
              {showSDLW && (
                <div className="dbo-cardcontext-flex">
                  <span className="dbo-context-text">
                    {formatMessage({ id: sameDayLastWeek })}
                  </span>
                  <PercentageItem percentage={sdlwRatio}></PercentageItem>
                </div>
              )}
            </>
          </MultiRingRatioBubble>
        </div>
        <div className="dbo-voice-keyMetric-info-chart">
          {seriesData.length > 0 && (
            <MetricChart
              xAxisData={xAxisData}
              seriesData={seriesData}
              rightTitle={formatMessage({ id: title })}
              filter={filter}
            />
          )}
        </div>
      </div>
    </CardCommon>
  );
}
